/* ══════════════════════════════════════════════════════════════
   Nifty 50 Company → NSE Symbol Mapping
   Used for entity extraction and news→stock correlation
   ══════════════════════════════════════════════════════════════ */

export interface CompanyEntry {
    symbol: string;
    name: string;
    sector: string;
    weight: number; // % weight in Nifty 50
}

export const NIFTY50_COMPANIES: CompanyEntry[] = [
    { symbol: 'RELIANCE', name: 'Reliance Industries', sector: 'energy', weight: 10.2 },
    { symbol: 'HDFCBANK', name: 'HDFC Bank', sector: 'banking', weight: 8.5 },
    { symbol: 'ICICIBANK', name: 'ICICI Bank', sector: 'banking', weight: 7.1 },
    { symbol: 'INFY', name: 'Infosys', sector: 'it', weight: 6.3 },
    { symbol: 'TCS', name: 'Tata Consultancy Services', sector: 'it', weight: 4.8 },
    { symbol: 'ITC', name: 'ITC Limited', sector: 'fmcg', weight: 4.5 },
    { symbol: 'HINDUNILVR', name: 'Hindustan Unilever', sector: 'fmcg', weight: 3.2 },
    { symbol: 'SBIN', name: 'State Bank of India', sector: 'banking', weight: 3.0 },
    { symbol: 'BHARTIARTL', name: 'Bharti Airtel', sector: 'telecom', weight: 3.5 },
    { symbol: 'KOTAKBANK', name: 'Kotak Mahindra Bank', sector: 'banking', weight: 2.8 },
    { symbol: 'LT', name: 'Larsen & Toubro', sector: 'infra', weight: 3.2 },
    { symbol: 'AXISBANK', name: 'Axis Bank', sector: 'banking', weight: 2.5 },
    { symbol: 'BAJFINANCE', name: 'Bajaj Finance', sector: 'nbfc', weight: 2.3 },
    { symbol: 'MARUTI', name: 'Maruti Suzuki', sector: 'auto', weight: 1.8 },
    { symbol: 'TATAMOTORS', name: 'Tata Motors', sector: 'auto', weight: 1.9 },
    { symbol: 'M&M', name: 'Mahindra & Mahindra', sector: 'auto', weight: 2.0 },
    { symbol: 'SUNPHARMA', name: 'Sun Pharmaceutical', sector: 'pharma', weight: 1.7 },
    { symbol: 'TATASTEEL', name: 'Tata Steel', sector: 'metal', weight: 1.3 },
    { symbol: 'NTPC', name: 'NTPC Limited', sector: 'energy', weight: 1.6 },
    { symbol: 'HCLTECH', name: 'HCL Technologies', sector: 'it', weight: 1.8 },
    { symbol: 'WIPRO', name: 'Wipro', sector: 'it', weight: 1.2 },
    { symbol: 'ADANIENT', name: 'Adani Enterprises', sector: 'conglom', weight: 1.5 },
    { symbol: 'ADANIPORTS', name: 'Adani Ports', sector: 'infra', weight: 1.3 },
    { symbol: 'POWERGRID', name: 'Power Grid Corporation', sector: 'energy', weight: 1.2 },
    { symbol: 'ONGC', name: 'Oil & Natural Gas Corp', sector: 'energy', weight: 1.1 },
    { symbol: 'DRREDDY', name: "Dr. Reddy's Laboratories", sector: 'pharma', weight: 1.0 },
    { symbol: 'ULTRACEMCO', name: 'UltraTech Cement', sector: 'cement', weight: 1.1 },
    { symbol: 'BAJAJFINSV', name: 'Bajaj Finserv', sector: 'nbfc', weight: 0.9 },
    { symbol: 'TECHM', name: 'Tech Mahindra', sector: 'it', weight: 0.8 },
    { symbol: 'NESTLEIND', name: 'Nestle India', sector: 'fmcg', weight: 0.8 },
    { symbol: 'TITAN', name: 'Titan Company', sector: 'consumer', weight: 1.2 },
    { symbol: 'COALINDIA', name: 'Coal India', sector: 'mining', weight: 0.7 },
    { symbol: 'JSWSTEEL', name: 'JSW Steel', sector: 'metal', weight: 0.8 },
    { symbol: 'HEROMOTOCO', name: 'Hero MotoCorp', sector: 'auto', weight: 0.6 },
    { symbol: 'CIPLA', name: 'Cipla', sector: 'pharma', weight: 0.7 },
    { symbol: 'BRITANNIA', name: 'Britannia Industries', sector: 'fmcg', weight: 0.6 },
    { symbol: 'GRASIM', name: 'Grasim Industries', sector: 'cement', weight: 0.7 },
    { symbol: 'EICHERMOT', name: 'Eicher Motors', sector: 'auto', weight: 0.6 },
    { symbol: 'DIVISLAB', name: "Divi's Laboratories", sector: 'pharma', weight: 0.5 },
    { symbol: 'APOLLOHOSP', name: 'Apollo Hospitals', sector: 'pharma', weight: 0.6 },
    { symbol: 'INDUSINDBK', name: 'IndusInd Bank', sector: 'banking', weight: 0.5 },
    { symbol: 'HINDALCO', name: 'Hindalco Industries', sector: 'metal', weight: 0.6 },
    { symbol: 'BAJAJ-AUTO', name: 'Bajaj Auto', sector: 'auto', weight: 0.5 },
    { symbol: 'BPCL', name: 'Bharat Petroleum', sector: 'energy', weight: 0.5 },
    { symbol: 'TATACONSUM', name: 'Tata Consumer Products', sector: 'fmcg', weight: 0.5 },
    { symbol: 'LTIM', name: 'LTIMindtree', sector: 'it', weight: 0.5 },
    { symbol: 'ASIANPAINT', name: 'Asian Paints', sector: 'consumer', weight: 0.7 },
    { symbol: 'SBILIFE', name: 'SBI Life Insurance', sector: 'insurance', weight: 0.5 },
    { symbol: 'TRENT', name: 'Trent Limited', sector: 'retail', weight: 0.4 },
    { symbol: 'SHRIRAMFIN', name: 'Shriram Finance', sector: 'nbfc', weight: 0.4 },
];

/** Build name→symbol lookup (case-insensitive) */
const _nameLookup = new Map<string, string>();
NIFTY50_COMPANIES.forEach(c => {
    _nameLookup.set(c.name.toLowerCase(), c.symbol);
    // Also add common short names
    const shortName = c.name.split(' ')[0].toLowerCase();
    if (!_nameLookup.has(shortName)) {
        _nameLookup.set(shortName, c.symbol);
    }
});

/** Find NSE symbol from company name */
export function findSymbolByName(name: string): string | undefined {
    return _nameLookup.get(name.toLowerCase());
}

/** Find company by symbol */
export function getCompanyBySymbol(symbol: string): CompanyEntry | undefined {
    return NIFTY50_COMPANIES.find(c => c.symbol === symbol);
}
